/* eslint-disable no-restricted-globals */
import { DATE_PIPE_DEFAULT_OPTIONS, registerLocaleData } from '@angular/common';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import localeEs from '@angular/common/locales/es';
import { ApplicationConfig, ErrorHandler, inject, isDevMode, provideAppInitializer, provideZoneChangeDetection } from '@angular/core';
import { LOCALE_ID } from '@angular/core';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideRouter, RouteReuseStrategy } from '@angular/router';
import { provideTransloco, TranslocoService } from '@jsverse/transloco';
import { ConfirmationService, MessageService } from 'primeng/api';
import { PrimeNG, providePrimeNG } from 'primeng/config';

import { routes } from './app.routes';
import { authSessionHttpInterceptor } from './auth/auth-session-http.interceptor';
import { AuthService } from './auth/auth.service';
import { AppConfirmationService } from './common/service/app-confirmation.service';
import { EntityFormRouteReuseStrategy } from './entity-form-route-reuse.strategy';
import { PermissionService } from './features/setting/permission.service';
import { browserSessionHttpInterceptor } from './global-error/browser-session-http.interceptor';
import { GlobalErrorHandlerService } from './global-error/global-error-handler.service';
import { globalErrorHttpInterceptor } from './global-error/global-error-http.interceptor';
import { AppGlobalErrorHandler } from './global-error/global-error.handler';
import { SUPPORTED_LANGUAGES, USER_LANGUAGE_KEY } from './language.constants';
import Nt25Preset from './nt25-preset';
import { initSuperTokensUI } from './supertokens-config';
import { TranslocoHttpLoader } from './transloco-loader';

registerLocaleData(localeEs);

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes),
    provideAnimationsAsync(),
    provideHttpClient(withInterceptors([browserSessionHttpInterceptor, globalErrorHttpInterceptor, authSessionHttpInterceptor])),
    providePrimeNG({
      theme: {
        preset: Nt25Preset,
        options: {
          darkModeSelector: '.my-app-dark'
        }
      }
    }),
    provideTransloco({
      config: {
        availableLangs: [...SUPPORTED_LANGUAGES],
        defaultLang: localStorage.getItem(USER_LANGUAGE_KEY) || 'es',
        fallbackLang: ['en', 'es'],
        missingHandler: {
          useFallbackTranslation: true,
          logMissingKey: false
        },
        reRenderOnLangChange: true,
        prodMode: !isDevMode()
      },
      loader: TranslocoHttpLoader
    }),
    provideAppInitializer(() => {
      inject(GlobalErrorHandlerService).initialize();
    }),
    provideAppInitializer(initSuperTokensUI),
    provideAppInitializer(async () => {
      const authService = inject(AuthService);
      const permissionService = inject(PermissionService);

      await authService.loadUser();
      await permissionService.loadPermissions();
    }),
    provideAppInitializer(() => {
      const primeng = inject(PrimeNG);
      const translocoService = inject(TranslocoService);

      const localeMap: Record<string, string> = {
        en: 'en-US',
        es: 'es-ES',
        de: 'de-DE',
        ru: 'ru-RU'
      };

      const applyLocale = (lang: string) => {
        const locale = localeMap[lang] || lang;
        const monthLong = new Intl.DateTimeFormat(locale, { month: 'long' });
        const monthShort = new Intl.DateTimeFormat(locale, { month: 'short' });
        const weekdayLong = new Intl.DateTimeFormat(locale, { weekday: 'long' });
        const weekdayShort = new Intl.DateTimeFormat(locale, { weekday: 'short' });
        const weekdayNarrow = new Intl.DateTimeFormat(locale, { weekday: 'narrow' });
        const weekStart = new Date(2020, 0, 5);

        primeng.setTranslation({
          monthNames: Array.from({ length: 12 }, (_, index) => monthLong.format(new Date(2020, index, 1))),
          monthNamesShort: Array.from({ length: 12 }, (_, index) => monthShort.format(new Date(2020, index, 1))),
          dayNames: Array.from({ length: 7 }, (_, index) => weekdayLong.format(new Date(weekStart.getTime() + index * 86400000))),
          dayNamesShort: Array.from({ length: 7 }, (_, index) => weekdayShort.format(new Date(weekStart.getTime() + index * 86400000))),
          dayNamesMin: Array.from({ length: 7 }, (_, index) => weekdayNarrow.format(new Date(weekStart.getTime() + index * 86400000)))
        });
      };

      applyLocale(translocoService.getActiveLang());
      translocoService.langChanges$.subscribe((lang) => applyLocale(lang));
    }),
    MessageService,
    { provide: ConfirmationService, useClass: AppConfirmationService },
    {
      provide: RouteReuseStrategy,
      useClass: EntityFormRouteReuseStrategy
    },
    {
      provide: ErrorHandler,
      useClass: AppGlobalErrorHandler
    },
    { provide: LOCALE_ID, useValue: 'es' },
    {
      provide: DATE_PIPE_DEFAULT_OPTIONS,
      useValue: { timezone: '+0000' }
    }
  ]
};
